import { archiveRepository } from './archive';
import { caseRepository } from './cases';
import { clientRepository } from './clients';
import { documentRepository } from './documents';
import { eventRepository } from './events';
import { openDatabase, type OpenedDatabase, type Storage } from './open';
import { searchRepository } from './search';
import { tagRepository } from './tags';

export type { Storage };
export type { CaseFilter } from './cases';
export type { SearchHit, SearchResults } from './search';
export type { RestoredCounts, TableRows } from './archive';
export { SCHEMA_VERSION } from './schema';
/* Идентификатор записи выдаётся до её создания: имя файла в хранилище — это он. */
export { newId } from './mappers';

/**
 * Слой данных целиком: репозитории по границам домена, как на Android.
 *
 * Экраны не знают ни про SQL, ни про то, где легли данные — в OPFS или в IndexedDB.
 */
export interface Repositories {
  cases: ReturnType<typeof caseRepository>;
  clients: ReturnType<typeof clientRepository>;
  documents: ReturnType<typeof documentRepository>;
  events: ReturnType<typeof eventRepository>;
  tags: ReturnType<typeof tagRepository>;
  search: ReturnType<typeof searchRepository>;
  /** Выгрузка и загрузка содержимого целиком — архив дел, [D-47](docs/DECISIONS.md). */
  archive: ReturnType<typeof archiveRepository>;
  storage: Storage;
  /** Режим журнала SQLite — видно на экране проверки хранилища. */
  journalMode: () => Promise<string>;
  close: () => Promise<void>;
}

export function repositories(db: OpenedDatabase, now: () => number = Date.now): Repositories {
  return {
    cases: caseRepository(db, now),
    clients: clientRepository(db, now),
    documents: documentRepository(db, now),
    events: eventRepository(db, now),
    tags: tagRepository(db, now),
    search: searchRepository(db),
    archive: archiveRepository(db),
    storage: db.storage,
    journalMode: async () => {
      const [row] = await db.query<{ journal_mode: string }>('PRAGMA journal_mode');
      return String(row?.journal_mode ?? '?');
    },
    close: () => db.close(),
  };
}

/** Открыть базу приложения и собрать репозитории. Вызывается один раз при старте. */
export async function openRepositories(): Promise<Repositories> {
  return repositories(await openDatabase());
}

/*
 * База в памяти для тестов живёт в `testing.ts` и **не экспортируется отсюда**.
 *
 * Даже динамический импорт затягивает модуль в сборку приложения, а он знает про
 * `node:fs` — сборка на этом падает. Тесты берут её напрямую: `import { … } from
 * './testing'`.
 */
